/* eslint-disable react/no-danger */
import React, { FC, useState, useRef, useEffect } from 'react';

import ChevronDown from '~/assets/icons/ChevronDown';
import ButtonSocialGroup from '~/components/button-social-group/ButtonSocialGroup';
import ImageWrapped from '~/components/image-wrapped/ImageWrapped';
import Link from '~/components/link/Link';
import useWindowSize from '~/components/utils/useWindowSize';
import { IAuthorBio } from '~/interfaces/components/AuthorBio';

import Contributors from './inner-components/Contributors/Contributors';

const AuthorBio: FC<IAuthorBio> = ({
  name,
  socialLinks,
  image,
  biography,
  authorLink,
  layout = 'default',
  border = false,
  separator = false,
  role,
  contributorText,
  contributors,
  collapsible,
}) => {
  const borderClass = border ? ' author--border' : '';
  const separatorClass = separator ? ' author--separator' : '';
  const [isExpanded, setIsExpanded] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const bioRef = useRef<HTMLDivElement>(null);
  const { width } = useWindowSize();

  const authorPageModifier = (originalClass: string) =>
    authorLink ? '' : ` ${originalClass}--author-page`;

  const isCollapsibleEnabled = collapsible?.enabled && biography;
  const maxHeight = collapsible?.maxHeight || 100;

  useEffect(() => {
    if (!isCollapsibleEnabled || !bioRef.current) return;

    const checkHeight = () => {
      const isMobile = width < 768;
      if (!isMobile) {
        setShowButton(false);
        return;
      }

      const bioHeight = bioRef.current?.scrollHeight || 0;
      setShowButton(bioHeight > maxHeight);
    };

    checkHeight();
  }, [isCollapsibleEnabled, maxHeight, width, biography]);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const collapsibleClass = isCollapsibleEnabled ? ' author--collapsible' : '';
  const collapsedClass =
    isCollapsibleEnabled && !isExpanded && showButton ? ' author--collapsed' : '';

  return (
    <div
      className={`author author__${layout}-layout${borderClass}${separatorClass}${authorPageModifier(
        'author',
      )}${collapsibleClass}${collapsedClass}`}
      data-hydrate={isCollapsibleEnabled ? 'true' : undefined}
    >
      <div className="author__header">
        {image && (
          <div className="author__avatar-block">
            <ImageWrapped {...image} className="author__avatar" dataHydrate={false} />
          </div>
        )}
        <div className="author__heading">
          <div className="author__name">
            {authorLink ? (
              <Link classname="author__name_link" href={authorLink.href}>
                {authorLink.text}
              </Link>
            ) : (
              <h1 className="author__name_text">{name}</h1>
            )}
          </div>
          {socialLinks && (
            <div className="author__social">
              <ButtonSocialGroup className="author__social-buttons" {...socialLinks} />
            </div>
          )}
          {role && <div className="author__role">{role}</div>}
        </div>
      </div>
      {biography &&
        (isCollapsibleEnabled ? (
          <div className="author__bio-wrapper">
            <div
              ref={bioRef}
              className="author__biography"
              dangerouslySetInnerHTML={{ __html: biography }}
            />
            {showButton && (
              <button
                className="author__toggle-button"
                onClick={handleToggle}
                aria-expanded={isExpanded}
                type="button"
              >
                <span className="author__toggle-text">
                  {isExpanded
                    ? collapsible?.readLessText || 'Read less'
                    : collapsible?.readMoreText || 'Read more'}
                </span>
                <ChevronDown
                  className={`author__toggle-icon${
                    isExpanded ? ' author__toggle-icon--rotated' : ''
                  }`}
                />
              </button>
            )}
          </div>
        ) : (
          <div className="author__biography" dangerouslySetInnerHTML={{ __html: biography }} />
        ))}
      {contributors && <Contributors contributors={contributors} text={contributorText} />}
    </div>
  );
};
export default AuthorBio;
